import axios from 'axios';
import { rememberHospitalBranding } from './hospitalBranding';

const isAuthFailure = (error) => [401, 403].includes(error?.response?.status);

/**
 * Confirms a saved staff session with the server.
 * Resolves to the fresh user object, or null when the session is missing, expired,
 * revoked, or belongs to another role (all saved keys are cleared in that case).
 */
export async function verifyStoredSession({ tokenKey, refreshKey, userKey, role, extraKeys = [] }) {
  const clear = () => {
    [tokenKey, refreshKey, userKey, ...extraKeys].forEach((key) => localStorage.removeItem(key));
    delete axios.defaults.headers.common['Authorization'];
  };

  const refresh = async () => {
    const refreshToken = localStorage.getItem(refreshKey);
    if (!refreshToken) return null;
    try {
      const response = await axios.post('/api/auth/refresh', { refreshToken });
      localStorage.setItem(tokenKey, response.data.token);
      return response.data.token;
    } catch (error) {
      return null;
    }
  };

  const fetchMe = (token) => axios.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });

  let token = localStorage.getItem(tokenKey) || await refresh();
  if (!token) {
    clear();
    return null;
  }

  let response;
  try {
    response = await fetchMe(token);
  } catch (error) {
    if (!isAuthFailure(error)) throw error;
    token = await refresh();
    if (!token) {
      clear();
      return null;
    }
    try {
      response = await fetchMe(token);
    } catch (retryError) {
      clear();
      return null;
    }
  }

  const serverUser = response.data?.user;
  if (!serverUser || serverUser.role !== role) {
    clear();
    return null;
  }

  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem(userKey) || '{}');
  } catch (error) {
    stored = {};
  }
  const user = { ...stored, ...serverUser };
  localStorage.setItem(userKey, JSON.stringify(user));
  rememberHospitalBranding(user);
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  return user;
}

export default verifyStoredSession;
